import React from 'react'
import { ThemeProvider as StyledComponentsThemeProvider, createGlobalStyle } from 'styled-components'
import { useDarkModeManager } from '../contexts/LocalStorage'
import styled from 'styled-components'
import { Text } from 'rebass'

export default function ThemeProvider({ children }) {
  const [darkMode] = useDarkModeManager()

  return <StyledComponentsThemeProvider theme={theme(darkMode)}>{children}</StyledComponentsThemeProvider>
}

const theme = (darkMode, color) => ({
  customColor: color,
  textColor: darkMode ? '#ffffff' : '#ffffff',
  logoColor: darkMode ? '#ffffff' : '#ffffff',
  appBg: '#16072B',
  panelColor:
    'linear-gradient(180deg, rgba(54, 61, 77, 0.25) 0%, rgba(49, 56, 69, 0.25) 49.48%, rgba(43, 48, 60, 0.25) 69.79%, rgba(28, 30, 39, 0.25) 100%)',

  uniswapPink: darkMode ? '#51C0E1' : '#31415E',
  mobileNavBg: darkMode ? '#333333' : '#ffffff',

  concreteGray: darkMode ? '#29C22F' : '#F2F4F8',
  inputBackground: darkMode ? '#121122' : '#E4E9F1',
  shadowColor: darkMode ? '#121122' : '#8C8CE3',
  mercuryGray: darkMode ? '#121122' : '#BCC8DC',

  text1: '#ffffff',
  text2: '#FFF2D1',
  text3: darkMode ? 'rgba(255, 255, 255, 0.6)' : '#888D9B',
  text4: darkMode ? 'rgba(255, 255, 255, 0.38)' : '#BCC8DC',
  text5: darkMode ? 'rgba(255, 255, 255, 0.38)' : '#D7DEEA',

  // special case text types
  white: '#FFFFFF',
  searchColor: '#C7C7C7',
  searchHover: 'rgba(255, 255, 255, 0.15)',
  btnBorder: darkMode ? '#4E4E4E' : 'rgba(17, 17, 17, 0.2)',
  optionBg: 'rgba(255, 255, 255, 0.3)',

  // backgrounds / greys
  bg1: darkMode ? '#121122' : '#F2F4F8',
  bg2: darkMode ? 'rgba(255, 255, 255, 0.05)' : '#E4E9F1',
  bg3: darkMode ? 'rgba(255, 255, 255, 0.09)' : '#D7DEEA',
  bg4: darkMode ? 'rgba(255, 255, 255, 0.12)' : '#BCC8DC',
  bg5: darkMode ? 'rgba(255, 255, 255, 0.14)' : '#A1B2CE',
  bg6: darkMode
    ? 'linear-gradient(180deg, rgba(54, 61, 77, 0.25) 0%, rgba(49, 56, 69, 0.25) 49.48%, rgba(43, 48, 60, 0.25) 69.79%, rgba(28, 30, 39, 0.25) 100%)'
    : 'linear-gradient(180deg, rgba(54, 61, 77, 0.25) 0%, rgba(49, 56, 69, 0.25) 49.48%, rgba(43, 48, 60, 0.25) 69.79%, rgba(28, 30, 39, 0.25) 100%)',
  //specialty colors
  modalBG: darkMode ? 'rgba(18, 17, 34, 0.75)' : 'rgba(18, 17, 34, 0.6)',
  advancedBG:
    'linear-gradient(180deg, rgba(54, 61, 77, 0.25) 0%, rgba(49, 56, 69, 0.25) 49.48%, rgba(43, 48, 60, 0.25) 69.79%, rgba(28, 30, 39, 0.25) 100%)',
  onlyLight: darkMode ? '#121122' : 'transparent',
  divider: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(17, 17, 17, 0.1)',
  componentBg1: darkMode
    ? 'linear-gradient(180deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.08) 49.48%, rgba(211, 211, 211, 0.08) 69.79%, rgba(255, 255, 255, 0.08) 100%)'
    : 'linear-gradient(180deg, rgba(54, 61, 77, 0.25) 0%, rgba(49, 56, 69, 0.25) 49.48%, rgba(43, 48, 60, 0.25) 69.79%, rgba(28, 30, 39, 0.25) 100%)',

  //primary colors
  primary1: darkMode ? '#6665DD' : '#6665DD',
  primary2: darkMode ? '#8C8CE3' : '#8C8CE3',
  primary3: darkMode ? '#ADADEB' : '#ADADEB',
  primary4: darkMode ? '#2C2CB5' : '#2C2CB5',
  primary5: darkMode ? '#242494' : '#242494',

  // color text
  primaryText1: '#FFFFFF',

  // secondary colors
  secondary1: darkMode ? '#51C0E1' : '#73CCE7',
  secondary2: darkMode ? '#73CCE7' : '#BAE6F3',
  secondary3: darkMode ? '#96D9ED' : '#DCF2F9',

  shadow1: darkMode ? '#DCF2F9' : '#31415E',
  footerSocials: darkMode ? '#FFF2D1' : '#FFFFFF',

  // other
  red1: '#FF6978',
  green: '#FFF2D1',
  yellow1: '#FFE600',
  yellow2: '#FFA770',
  link: '#ffffff',
  dark: '#39404E',
  blue: '#73CCE7',

  background: darkMode
    ? 'radial-gradient(135.95% 135.95% at 50% -35.95%, rgba(102, 102, 255, 0.5) 0%, rgba(18, 17, 34, 0) 100%)'
    : '#B3B3B3',
})

const TextWrapper = styled(Text)`
  color: ${({ color, theme }) => theme[color]};
`

export const TYPE = {
  main(props) {
    return <TextWrapper fontWeight={500} fontSize={14} color={'textColor'} {...props} />
  },

  body(props) {
    return <TextWrapper fontWeight={400} fontSize={14} color={'textColor'} {...props} />
  },

  middle(props) {
    return <TextWrapper fontWeight={400} fontSize={18} color={'textColor'} {...props} />
  },

  small(props) {
    return <TextWrapper fontWeight={500} fontSize={12} color={'textColor'} {...props} />
  },

  header(props) {
    return <TextWrapper fontWeight={600} color={'text1'} {...props} />
  },

  middleHeader(props) {
    return <TextWrapper fontWeight={700} color={'textColor'} fontSize={24} {...props} />
  },

  largeHeader(props) {
    return <TextWrapper fontWeight={700} color={'textColor'} fontSize={36} {...props} />
  },

  light(props) {
    return <TextWrapper fontWeight={400} color={'text3'} fontSize={14} {...props} />
  },

  pink(props) {
    return <TextWrapper fontWeight={props.faded ? 400 : 600} color={props.faded ? 'text1' : 'text1'} {...props} />
  },

  dark(props) {
    return <TextWrapper fontWeight={400} fontSize={14} color={'dark'} {...props} />
  },
}

export const Hover = styled.div`
  :hover {
    cursor: pointer;
  }
`

export const Link = styled.a.attrs({
  target: '_blank',
  rel: 'noopener noreferrer',
})`
  text-decoration: none;
  cursor: pointer;
  color: ${({ theme }) => theme.primary1};
  font-weight: 500;
  :hover {
    text-decoration: underline;
  }
  :focus {
    outline: none;
    text-decoration: underline;
  }
  :active {
    text-decoration: none;
  }
`

export const ThemedBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  pointer-events: none;
  max-width: 100vw !important;
  height: 200vh;
  mix-blend-mode: color;
  //background: ${({ backgroundColor }) =>
    `radial-gradient(50% 50% at 50% 50%, ${backgroundColor} 0%, rgba(255, 255, 255, 0) 100%)`};
  position: absolute;
  top: 0px;
  left: 0px;
  /* z-index: ; */

  transform: translateY(-110vh);
`

export const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');
  html { font-family: 'IBM Plex Sans', sans-serif; }
  
  html,
  body {
    margin: 0;
    padding: 0;
    width: 100%;
    height: 100%;
    font-size: 14px;    
    background: ${({ theme }) => theme.appBg};
  }

  a {
    text-decoration: none;

    :hover {
      text-decoration: none
    }
  }

  
.three-line-legend {
	width: 100%;
	height: 70px;
	position: absolute;
	padding: 8px;
	font-size: 12px;
	color: #ffffff;
	background-color: rgba(255, 255, 255, 0.23);
	text-align: left;
	z-index: 10;
  pointer-events: none;
}

.three-line-legend-dark {
	width: 100%;
	height: 70px;
	position: absolute;
	padding: 8px;
	font-size: 12px;
	color: #ffffff;
	background-color: rgba(255, 255, 255, 0.23);
	text-align: left;
	z-index: 10;
  pointer-events: none;
}

@media screen and (max-width: 800px) {
  .three-line-legend {
    display: none !important;
  }
}

.tv-lightweight-charts{
  width: 100% !important;
  

  & > * {
    width: 100% !important;
  }
}


  html {
    font-size: 1rem;
    font-variant: none;
    color: 'black';
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    height: 100%;
  }

`
